import moment from 'moment'
import { useEffect, useMemo, useRef, useState } from 'react'
import { BAND_APPOINTMENTS_QUERY, BAND_CONTACTS_QUERY } from './graphql'
import { COHORT_PAGE, CohortRead, readAllPages } from './cohortRead'
import type { ActionCenterBandAppointmentsQuery, ActionCenterBandContactsQuery } from 'app/generated/gql/graphql'
import { useCdtCodeMappings } from 'app/hooks/useCdtCodeMappings'
import { gqlQuery } from 'app/lib/graphqlClient'
import type { BookedGroup } from 'app/types/treatment'
import { bookedGroupOf, futureVisitKinds } from 'lib/treatmentBooked'
import { TreatmentContact } from 'lib/treatmentCards'
import { logError } from 'lib/logging'

type Result = CohortRead<TreatmentContact>

type Fetched = { visits: Visits; contacts: TreatmentContact[] }
type Visits = Parameters<typeof futureVisitKinds>[0]
type State = Result | (Fetched & { ready: `fetched` })

const IDLE: Result = { ready: true, contacts: [] }
const READING: Result = { ready: false, contacts: [] }

/**
 * The band with one booked group taken out, decided from the schedule
 * because the contact index cannot tell a cleaning from a crown. Reads the
 * future schedule in scope and the whole of what the index left in, then the
 * board pages the survivors itself; deciding per page of twenty-four would
 * shrink each page and leave the count wrong.
 */
export function useScheduleSplit(
  enabled: boolean,
  {
    search,
    sortField,
    direction,
    locationTerm,
    hidden,
  }: {
    search: string
    sortField: string
    direction: `ASC` | `DESC`
    locationTerm?: string
    hidden: BookedGroup[]
  },
): Result {
  const [state, setState] = useState<State>(enabled ? READING : IDLE)
  const generation = useRef(0)
  const hiddenKey = hidden.join(`,`)
  const { categoryOf } = useCdtCodeMappings()

  useEffect(() => {
    const token = ++generation.current
    if (!enabled) {
      setState(IDLE)
      return
    }
    setState(READING)

    const run = async () => {
      const today = moment.utc().format(`YYYY-MM-DD`)
      const scheduleSearch = [`date:[${today} TO *]`, ...(locationTerm ? [locationTerm] : [])].join(` AND `)
      const [visits, contacts] = await Promise.all([
        readAllPages(`schedule`, async (index) => {
          const result = await gqlQuery<ActionCenterBandAppointmentsQuery>(BAND_APPOINTMENTS_QUERY, {
            search: scheduleSearch,
            pagination: { limit: COHORT_PAGE, page: index },
            sort: { field: `date`, direction: `ASC` },
          })
          if (result.error) throw result.error
          if (token !== generation.current) return undefined
          const data = result.data?.appointments
          return { hits: data?.hits ?? [], total: data?.total ?? 0 }
        }),
        readAllPages(`band`, async (index) => {
          const result = await gqlQuery<ActionCenterBandContactsQuery>(BAND_CONTACTS_QUERY, {
            search,
            pagination: { limit: COHORT_PAGE, page: index },
            sort: { field: sortField, direction },
          })
          if (result.error) throw result.error
          if (token !== generation.current) return undefined
          const data = result.data?.contacts
          return { hits: data?.hits ?? [], total: data?.total ?? 0 }
        }),
      ])
      if (token !== generation.current) return
      setState({ ready: `fetched`, visits, contacts })
    }

    run().catch((err) => {
      if (token !== generation.current) return
      logError(`Action Center: treatment schedule split`, err)
      setState({ ready: true, contacts: [], error: `These cases could not be loaded.` })
    })
  }, [enabled, search, sortField, direction, locationTerm])

  return useMemo<Result>(() => {
    if (state.ready !== `fetched`) return state
    const kinds = futureVisitKinds(state.visits, categoryOf)
    const kept = state.contacts.filter((contact) => {
      const group = bookedGroupOf(contact, kinds)
      return group === undefined || !hidden.includes(group)
    })
    return { ready: true, contacts: kept }
  }, [state, hiddenKey, categoryOf])
}
